import * as OpenCC from 'opencc-js';

import {
  KANGXI_DICT_BASE64,
  NUMEROLOGY_81_LUCK,
  NUMEROLOGY_81_MEANING,
  NUMEROLOGY_81_SCORE,
} from '../data/kangxiData';
import { lookupNameologyStroke } from '../data/nameologyStrokeMap';
import { NUMEROLOGY_81_META } from '../data/numerology81Full';
import { NAMING_CANDIDATE_CHAR_MAP } from '../data/namingCandidateChars.generated';

export type Wuxing = '金' | '木' | '水' | '火' | '土';
export type LuckLevel = '吉' | '凶' | '半吉半凶' | '平';
export type StageLevel = '大吉' | '吉' | '平' | '小凶' | '凶';
export type NamingGender = 'male' | 'female';
export type NamingGenderFit = 'good' | 'mixed' | 'bad';
type NamingLuckBucket = 'great' | 'good' | 'slight_good' | 'mixed' | 'slight_bad' | 'bad' | 'very_bad';

type GridKey = 'tiange' | 'renge' | 'dige' | 'biange' | 'waige' | 'zongge';
type StageKey = 'shaonian' | 'qingnian' | 'zhuangnian' | 'zhongnian' | 'laonian' | 'jiating';

export interface SurnameStrokeLookup {
  char: string;
  stroke: number;
  traditional: string;
  source: 'nameology' | 'local' | 'fallback' | 'none';
}

export interface CandidateNameChar {
  char: string;
  stroke: number;
  wuxing: Wuxing;
}

export interface NamingGridSnapshot {
  key: GridKey;
  label: string;
  rawValue: number;
  value81: number;
  wuxing: Wuxing;
  luckLevel: LuckLevel;
  luckTag81: string;
  score81: number;
  meaning81: string;
  chartLuck: string;
  genderLuck: string;
}

export interface NamingStageSnapshot {
  key: StageKey;
  label: string;
  level: StageLevel;
  summary: string;
}

export interface StrokeComboRecommendation {
  key: string;
  gender: NamingGender;
  surnameStroke: number;
  secondStroke: number;
  thirdStroke: number;
  secondWuxing: Wuxing;
  thirdWuxing: Wuxing;
  score: number;
  tags: string[];
  summary: string;
  secondCandidateCount: number;
  thirdCandidateCount: number;
  genderFit: {
    coreBadCount: number;
    coreMixedCount: number;
    minorBadCount: number;
    minorMixedCount: number;
    coreScoreTotal: number;
    minorScoreTotal: number;
    scoreTotal: number;
    allCoreCompatible: boolean;
  };
  threeTalents: {
    sequence: string;
    relationTR: string;
    relationRD: string;
    summary: string;
  };
  grids: Record<GridKey, NamingGridSnapshot>;
  stages: NamingStageSnapshot[];
}

const SIMPLE_TO_TRAD = OpenCC.Converter({ from: 'cn', to: 'hk' });
const NUM_ELEMENT_MAP: Record<number, Wuxing> = {
  0: '水',
  1: '木',
  2: '木',
  3: '火',
  4: '火',
  5: '土',
  6: '土',
  7: '金',
  8: '金',
  9: '水',
};

const LUCK_GOOD = new Set([
  1, 3, 5, 6, 7, 8, 11, 13, 15, 16, 17, 18, 21, 23, 24, 25, 29, 31, 32, 33, 35, 37, 39, 41, 45,
  47, 48, 52, 57, 63, 65, 68, 81,
]);

const LUCK_BAD = new Set([
  2, 4, 9, 10, 12, 14, 19, 20, 22, 26, 28, 34, 36, 40, 42, 43, 44, 46, 49, 50, 54, 56, 58, 59, 60, 62,
  64, 66, 69, 70, 71, 72, 74, 76, 79, 80,
]);

const LUCK_HALF = new Set([19, 22, 27, 30, 36, 38, 40, 49, 50, 51, 53, 55, 58, 73, 75, 77, 78]);

const GENERATE: Record<Wuxing, Wuxing> = {
  木: '火',
  火: '土',
  土: '金',
  金: '水',
  水: '木',
};

const CONTROL: Record<Wuxing, Wuxing> = {
  木: '土',
  土: '水',
  水: '火',
  火: '金',
  金: '木',
};

const KANGXI_STROKE_OVERRIDES: Record<string, number> = {
  罗: 19,
  羅: 19,
  诸: 15,
  諸: 15,
  淑: 12,
};

let packedBytes: Uint8Array | null = null;

const decodeBase64 = (input: string): Uint8Array => {
  const raw = String(input || '');
  if (!raw) return new Uint8Array(0);
  const binary = typeof atob === 'function' ? atob(raw) : Buffer.from(raw, 'base64').toString('binary');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const getPackedBytes = (): Uint8Array => {
  if (packedBytes) return packedBytes;
  packedBytes = decodeBase64(KANGXI_DICT_BASE64);
  return packedBytes;
};

const normalize81 = (value: number): number => {
  if (value <= 81) return value;
  return ((value - 81) % 80) + 1;
};

const normalizeGenderLuckText = (raw: string): string => String(raw || '').replace(/\s+/g, '').trim();
const normalizeLuckDescriptor = (raw: string): string => normalizeGenderLuckText(raw).replace(/^男女吉凶[:：]?/, '');

export const getWuxingByStroke = (stroke: number): Wuxing => {
  const tail = normalize81(stroke) % 10;
  return NUM_ELEMENT_MAP[tail];
};

const getLuckLevel = (value81: number): LuckLevel => {
  if (LUCK_HALF.has(value81)) return '半吉半凶';
  if (LUCK_GOOD.has(value81)) return '吉';
  if (LUCK_BAD.has(value81)) return '凶';
  const raw = String(NUMEROLOGY_81_LUCK[value81 - 1] || '').trim();
  if (raw.includes('半')) return '半吉半凶';
  if (raw.includes('吉') && !raw.includes('凶')) return '吉';
  if (raw.includes('凶') && !raw.includes('吉')) return '凶';
  return '平';
};

const getLuckScore = (level: LuckLevel): number => {
  if (level === '吉') return 2;
  if (level === '半吉半凶') return 0;
  if (level === '凶') return -2;
  return -1;
};

const getGridGenderLuck = (value81: number, luckLevel: LuckLevel): string => {
  const fromMeta = normalizeGenderLuckText(NUMEROLOGY_81_META[value81]?.genderLuck || '');
  if (fromMeta) return fromMeta;
  if (luckLevel === '半吉半凶') return '男女参半';
  if (luckLevel === '凶') return '男女皆凶';
  return '男女皆吉';
};

const getGridChartLuck = (value81: number, luckLevel: LuckLevel): string => {
  const fromMeta = normalizeLuckDescriptor(NUMEROLOGY_81_META[value81]?.chartLuck || '');
  if (fromMeta) return fromMeta;
  if (luckLevel === '半吉半凶') return '半吉半凶';
  if (luckLevel === '凶') return '凶';
  return '吉';
};

const extractGenderSpecificLuck = (genderLuck: string, gender: NamingGender): string => {
  const text = normalizeLuckDescriptor(genderLuck);
  if (!text) return '';

  if (text.startsWith('男女皆') || text.startsWith('男女均')) {
    return normalizeLuckDescriptor(text.replace(/^男女[皆均]/, ''));
  }

  if (text.includes('男') && text.includes('女')) {
    const match = gender === 'male'
      ? text.match(/男([^男女]+)/)
      : text.match(/女([^男女]+)/);
    if (match?.[1]) return normalizeLuckDescriptor(match[1]);
  }

  return text;
};

const classifyGenderLuckValue = (raw: string): NamingGenderFit => {
  const text = normalizeLuckDescriptor(raw);
  if (!text) return 'mixed';
  if (text.includes('参半') || text.includes('半吉') || text.includes('半凶') || (text.includes('吉') && text.includes('凶'))) {
    return 'mixed';
  }
  if (text.includes('吉')) return 'good';
  if (text.includes('凶')) return 'bad';
  return 'mixed';
};

const scoreGenderLuckValue = (raw: string): number => {
  const text = normalizeLuckDescriptor(raw);
  if (!text) return 0;
  if (text.includes('参半') || text === '半吉半凶' || text === '半凶半吉') return 0;
  if (text.includes('吉') && text.includes('凶')) {
    if (text === '凶后吉') return 0.5;
    if (text === '吉后凶') return 0;
    return 0;
  }
  if (text.includes('大吉')) return 2.6;
  if (text.includes('半吉')) return 1.1;
  if (text.includes('吉')) return 2;
  if (text.includes('大凶')) return -2.6;
  if (text.includes('半凶')) return -1.1;
  if (text.includes('凶')) return -2;
  return 0;
};

const getLuckBucket = (raw: string): NamingLuckBucket => {
  const text = normalizeLuckDescriptor(raw);
  if (!text) return 'mixed';
  if (text.includes('大凶')) return 'very_bad';
  if (text.includes('大吉')) return 'great';
  if (text === '凶后吉') return 'slight_good';
  if (text === '吉后凶' || text === '半吉半凶' || text === '半凶半吉' || text.includes('参半') || (text.includes('吉') && text.includes('凶'))) {
    return 'mixed';
  }
  if (text.includes('半凶')) return 'slight_bad';
  if (text.includes('半吉')) return 'slight_good';
  if (text.includes('凶')) return 'bad';
  if (text.includes('吉')) return 'good';
  return 'mixed';
};

const getLuckBucketGrade = (bucket: NamingLuckBucket): number => {
  switch (bucket) {
    case 'great':
      return 100;
    case 'good':
      return 92;
    case 'slight_good':
      return 80;
    case 'mixed':
      return 64;
    case 'slight_bad':
      return 45;
    case 'bad':
      return 18;
    case 'very_bad':
      return 6;
    default:
      return 64;
  }
};

const isGoodBucket = (bucket: NamingLuckBucket): boolean => bucket === 'great' || bucket === 'good';
const isMixedBucket = (bucket: NamingLuckBucket): boolean => bucket === 'mixed' || bucket === 'slight_good' || bucket === 'slight_bad';
const isHardBadBucket = (bucket: NamingLuckBucket): boolean => bucket === 'bad' || bucket === 'very_bad';

const getEffectiveGridLuckText = (grid: NamingGridSnapshot, gender: NamingGender): string => (
  extractGenderSpecificLuck(grid.genderLuck, gender) || normalizeLuckDescriptor(grid.chartLuck)
);

const getThreeTalentGrade = (score: number): number => {
  if (score >= 2) return 100;
  if (score >= 1.5) return 95;
  if (score >= 1) return 88;
  if (score >= 0.5) return 80;
  if (score >= 0) return 70;
  if (score >= -0.5) return 58;
  if (score >= -1) return 46;
  if (score >= -1.5) return 34;
  return 20;
};

export const getNamingGridGenderFit = (genderLuck: string, gender: NamingGender): NamingGenderFit => {
  return classifyGenderLuckValue(extractGenderSpecificLuck(genderLuck, gender));
};

const getNamingGridGenderScore = (genderLuck: string, gender: NamingGender): number => (
  scoreGenderLuckValue(extractGenderSpecificLuck(genderLuck, gender))
);

const toStageLevel = (score: number): StageLevel => {
  if (score >= 3) return '大吉';
  if (score >= 1.5) return '吉';
  if (score >= 0) return '平';
  if (score >= -1.5) return '小凶';
  return '凶';
};

const describeRelation = (a: Wuxing, b: Wuxing): { text: string; score: number } => {
  if (a === b) return { text: '比和', score: 0.5 };
  if (GENERATE[a] === b) return { text: `${a}生${b}`, score: 1 };
  if (GENERATE[b] === a) return { text: `${a}受${b}生`, score: 0.5 };
  if (CONTROL[a] === b) return { text: `${a}克${b}`, score: -1 };
  if (CONTROL[b] === a) return { text: `${a}受${b}克`, score: -1.5 };
  return { text: `${a}与${b}关系平`, score: 0 };
};

const createGridSnapshot = (key: GridKey, label: string, rawValue: number): NamingGridSnapshot => {
  const value81 = normalize81(rawValue);
  const luckLevel = getLuckLevel(value81);
  return {
    key,
    label,
    rawValue,
    value81,
    wuxing: getWuxingByStroke(value81),
    luckLevel,
    luckTag81: String(NUMEROLOGY_81_LUCK[value81 - 1] || '').trim(),
    score81: Number(NUMEROLOGY_81_SCORE[value81 - 1] || 0),
    meaning81: String(NUMEROLOGY_81_MEANING[value81 - 1] || '').trim(),
    chartLuck: getGridChartLuck(value81, luckLevel),
    genderLuck: getGridGenderLuck(value81, luckLevel),
  };
};

const buildPairStage = (
  key: StageKey,
  label: string,
  factors: string,
  first: NamingGridSnapshot,
  second: NamingGridSnapshot,
): NamingStageSnapshot => {
  const relation = describeRelation(first.wuxing, second.wuxing);
  const score = getLuckScore(first.luckLevel) + getLuckScore(second.luckLevel) + relation.score;
  const level = toStageLevel(score);
  return {
    key,
    label,
    level,
    summary: `${factors}呈${relation.text}，综合偏${level}。`,
  };
};

const buildSingleStage = (
  key: StageKey,
  label: string,
  target: NamingGridSnapshot,
): NamingStageSnapshot => ({
  key,
  label,
  level: toStageLevel(getLuckScore(target.luckLevel)),
  summary: `${target.label}${target.value81}数主${toStageLevel(getLuckScore(target.luckLevel))}。`,
});

const lookupPackedStroke = (char: string): number => {
  const cp = char.codePointAt(0);
  if (!cp || cp < 0x4e00 || cp > 0x9fa5) return 0;
  const bytes = getPackedBytes();
  const idx = (cp - 0x4e00) * 2;
  if (idx < 0 || idx + 1 >= bytes.length) return 0;
  const stroke = Number(bytes[idx] || 0);
  return Number.isFinite(stroke) && stroke > 0 ? stroke : 0;
};

export const lookupLocalSurnameStroke = (rawChar: string): SurnameStrokeLookup => {
  const char = Array.from(String(rawChar || '').trim())[0] || '';
  if (!char) {
    return { char: '', stroke: 0, traditional: '', source: 'none' };
  }

  const traditional = SIMPLE_TO_TRAD(char);
  const nameologyStroke = lookupNameologyStroke(char) || lookupNameologyStroke(traditional);
  if (nameologyStroke > 0) {
    return {
      char,
      stroke: nameologyStroke,
      traditional,
      source: 'nameology',
    };
  }

  if (KANGXI_STROKE_OVERRIDES[char]) {
    return {
      char,
      stroke: KANGXI_STROKE_OVERRIDES[char],
      traditional,
      source: 'fallback',
    };
  }

  const directStroke = lookupPackedStroke(char);
  if (directStroke > 0) {
    return {
      char,
      stroke: directStroke,
      traditional,
      source: 'local',
    };
  }

  if (traditional && traditional !== char) {
    if (KANGXI_STROKE_OVERRIDES[traditional]) {
      return {
        char,
        stroke: KANGXI_STROKE_OVERRIDES[traditional],
        traditional,
        source: 'fallback',
      };
    }
    const tradStroke = lookupPackedStroke(traditional);
    if (tradStroke > 0) {
      return {
        char,
        stroke: tradStroke,
        traditional,
        source: 'local',
      };
    }
  }

  return { char, stroke: 0, traditional: traditional || '', source: 'none' };
};

export const getCandidateCharsByStroke = (stroke: number): CandidateNameChar[] => {
  const normalizedStroke = Math.max(0, Math.trunc(Number(stroke) || 0));
  if (normalizedStroke <= 0) return [];
  const chars = NAMING_CANDIDATE_CHAR_MAP[normalizedStroke] || [];
  const wuxing = getWuxingByStroke(normalizedStroke);
  return chars.map((char) => ({ char, stroke: normalizedStroke, wuxing }));
};

export const getCandidateCountByStroke = (stroke: number): number => getCandidateCharsByStroke(stroke).length;

export const recommendStrokeCombos = (params: {
  surnameStroke: number;
  gender?: NamingGender;
  limit?: number;
  minStroke?: number;
  maxStroke?: number;
}): StrokeComboRecommendation[] => {
  const surnameStroke = Math.max(0, Math.trunc(Number(params.surnameStroke) || 0));
  if (surnameStroke <= 0) return [];

  const gender: NamingGender = params.gender === 'female' ? 'female' : 'male';
  const limit = Math.max(1, Math.trunc(Number(params.limit) || 18));
  const minStroke = Math.max(1, Math.trunc(Number(params.minStroke) || 1));
  const maxStroke = Math.max(minStroke, Math.trunc(Number(params.maxStroke) || 30));
  const list: StrokeComboRecommendation[] = [];

  for (let secondStroke = minStroke; secondStroke <= maxStroke; secondStroke += 1) {
    const secondCandidateCount = getCandidateCountByStroke(secondStroke);
    if (secondCandidateCount <= 0) continue;

    for (let thirdStroke = minStroke; thirdStroke <= maxStroke; thirdStroke += 1) {
      const thirdCandidateCount = getCandidateCountByStroke(thirdStroke);
      if (thirdCandidateCount <= 0) continue;

      const grids: Record<GridKey, NamingGridSnapshot> = {
        tiange: createGridSnapshot('tiange', '天格', surnameStroke + 1),
        renge: createGridSnapshot('renge', '人格', surnameStroke + secondStroke),
        dige: createGridSnapshot('dige', '地格', secondStroke + thirdStroke),
        biange: createGridSnapshot('biange', '变格', surnameStroke + thirdStroke),
        waige: createGridSnapshot('waige', '外格', thirdStroke + 1),
        zongge: createGridSnapshot('zongge', '总格', surnameStroke + secondStroke + thirdStroke),
      };

      const relationTR = describeRelation(grids.tiange.wuxing, grids.renge.wuxing);
      const relationRD = describeRelation(grids.renge.wuxing, grids.dige.wuxing);
      const stages: NamingStageSnapshot[] = [
        buildPairStage('shaonian', '少年运', '天格+地格', grids.tiange, grids.dige),
        buildPairStage('qingnian', '青年运', '人格+地格', grids.renge, grids.dige),
        buildPairStage('zhuangnian', '壮年运', '天格+人格', grids.tiange, grids.renge),
        buildPairStage('zhongnian', '中年运', '人格+外格', grids.renge, grids.waige),
        buildSingleStage('laonian', '老年运', grids.zongge),
        buildSingleStage('jiating', '家庭运', grids.zongge),
      ];

      const coreGenderGrids = [grids.renge, grids.dige, grids.zongge];
      const minorGenderGrids = [grids.tiange, grids.waige, grids.biange];
      const coreLuckTexts = coreGenderGrids.map((grid) => getEffectiveGridLuckText(grid, gender));
      const minorLuckTexts = minorGenderGrids.map((grid) => getEffectiveGridLuckText(grid, gender));
      const coreBuckets = coreLuckTexts.map(getLuckBucket);
      const minorBuckets = minorLuckTexts.map(getLuckBucket);
      const coreBadCount = coreBuckets.filter(isHardBadBucket).length;
      const coreSoftBadCount = coreBuckets.filter((bucket) => bucket === 'slight_bad').length;
      const coreMixedCount = coreBuckets.filter((bucket) => isMixedBucket(bucket)).length;
      const minorGenderBadCount = minorBuckets.filter(isHardBadBucket).length;
      const minorSoftBadCount = minorBuckets.filter((bucket) => bucket === 'slight_bad').length;
      const minorGenderMixedCount = minorBuckets.filter((bucket) => isMixedBucket(bucket)).length;
      const coreGenderScoreTotal = coreLuckTexts.reduce((sum, text) => sum + getNamingGridGenderScore(text, gender), 0);
      const minorGenderScoreTotal = minorLuckTexts.reduce((sum, text) => sum + getNamingGridGenderScore(text, gender), 0);
      const allCoreGenderCompatible = coreBadCount === 0 && coreSoftBadCount === 0;
      const allCoreGood = coreBuckets.every(isGoodBucket);
      const allGridsGood = [...coreBuckets, ...minorBuckets].every(isGoodBucket);
      const coreAvg = (
        getLuckBucketGrade(coreBuckets[0] || 'mixed') * 0.38
        + getLuckBucketGrade(coreBuckets[1] || 'mixed') * 0.30
        + getLuckBucketGrade(coreBuckets[2] || 'mixed') * 0.32
      );
      const minorAvg = (
        getLuckBucketGrade(minorBuckets[0] || 'mixed') * 0.34
        + getLuckBucketGrade(minorBuckets[1] || 'mixed') * 0.28
        + getLuckBucketGrade(minorBuckets[2] || 'mixed') * 0.38
      );
      const threeTalentScore = relationTR.score + relationRD.score;
      const threeTalentGrade = getThreeTalentGrade(threeTalentScore);
      const baseScore = coreAvg * 0.64 + minorAvg * 0.14 + threeTalentGrade * 0.22;
      const bonus = (
        (allCoreGood ? 3 : 0)
        + (allGridsGood ? 2 : 0)
        + (allCoreGenderCompatible ? 2 : 0)
        + (threeTalentScore >= 1.5 ? 3 : threeTalentScore >= 1 ? 1.5 : 0)
      );
      const penalty = (
        coreBadCount * 16
        + coreSoftBadCount * 10
        + coreMixedCount * 5
        + minorGenderBadCount * 6
        + minorSoftBadCount * 4
        + minorGenderMixedCount * 1.5
      );
      let score = Math.round(baseScore + bonus - penalty);

      if (coreBadCount > 0) {
        score = Math.min(score, 79 - Math.max(0, coreBadCount - 1) * 4);
      } else if (coreSoftBadCount > 0 || coreMixedCount > 0) {
        score = Math.min(score, 88 - Math.max(0, coreSoftBadCount + coreMixedCount - 1) * 2);
      } else if (minorGenderBadCount > 0) {
        score = Math.min(score, 93 - Math.max(0, minorGenderBadCount - 1) * 3);
      } else if (minorSoftBadCount > 0 || minorGenderMixedCount > 0) {
        score = Math.min(score, 97 - Math.max(0, minorSoftBadCount + minorGenderMixedCount - 1) * 2);
      }

      const nonTiangeAllGood = [
        grids.renge,
        grids.dige,
        grids.biange,
        grids.waige,
        grids.zongge,
      ].every((grid) => isGoodBucket(getLuckBucket(getEffectiveGridLuckText(grid, gender))));
      const tiangeGood = isGoodBucket(getLuckBucket(getEffectiveGridLuckText(grids.tiange, gender)));
      if (nonTiangeAllGood) {
        score = Math.max(score, tiangeGood ? 97 : 95);
      }

      score = Math.max(1, Math.min(100, score));

      const tags: string[] = [];
      if (allCoreGood) tags.push('主格全吉');
      if (allGridsGood) tags.push('六格顺');
      if (coreBadCount === 0 && coreSoftBadCount === 0) tags.push('无主凶');
      if (allCoreGenderCompatible) tags.push(gender === 'female' ? '适配女名' : '适配男名');
      if (threeTalentScore >= 1.5) tags.push('三才贯通');
      else if (relationTR.score >= 0 && relationRD.score >= 0) tags.push('三才顺');
      if (relationTR.score > 0 || relationRD.score > 0) tags.push('有相生');
      if (grids.renge.luckLevel === '吉') tags.push(`人格${grids.renge.value81}`);
      if (grids.zongge.luckLevel === '吉') tags.push(`总格${grids.zongge.value81}`);

      list.push({
        key: `${secondStroke}-${thirdStroke}`,
        gender,
        surnameStroke,
        secondStroke,
        thirdStroke,
        secondWuxing: getWuxingByStroke(secondStroke),
        thirdWuxing: getWuxingByStroke(thirdStroke),
        score,
        tags: Array.from(new Set(tags)).slice(0, 4),
        summary: `人格${grids.renge.value81}${grids.renge.chartLuck}，地格${grids.dige.value81}${grids.dige.chartLuck}，总格${grids.zongge.value81}${grids.zongge.chartLuck}，三才${grids.tiange.wuxing}-${grids.renge.wuxing}-${grids.dige.wuxing}。`,
        secondCandidateCount,
        thirdCandidateCount,
        genderFit: {
          coreBadCount,
          coreMixedCount: coreMixedCount + coreSoftBadCount,
          minorBadCount: minorGenderBadCount,
          minorMixedCount: minorGenderMixedCount + minorSoftBadCount,
          coreScoreTotal: Number(coreGenderScoreTotal.toFixed(2)),
          minorScoreTotal: Number(minorGenderScoreTotal.toFixed(2)),
          scoreTotal: Number((coreGenderScoreTotal + minorGenderScoreTotal).toFixed(2)),
          allCoreCompatible: allCoreGenderCompatible,
        },
        threeTalents: {
          sequence: `${grids.tiange.wuxing}-${grids.renge.wuxing}-${grids.dige.wuxing}`,
          relationTR: relationTR.text,
          relationRD: relationRD.text,
          summary: relationTR.score + relationRD.score >= 1.5
            ? '三才以相生为主，整体气场顺畅。'
            : relationTR.score + relationRD.score >= 0
              ? '三才有生有和，整体偏稳。'
              : relationTR.score + relationRD.score >= -1
                ? '三才存在克制，阶段起伏会更明显。'
                : '三才克制偏重，选名时宜优先避开。',
        },
        grids,
        stages,
      });
    }
  }

  const sorted = list
    .sort((a, b) => (
      b.score - a.score
      || Number(b.genderFit.allCoreCompatible) - Number(a.genderFit.allCoreCompatible)
      || a.genderFit.coreBadCount - b.genderFit.coreBadCount
      || a.genderFit.coreMixedCount - b.genderFit.coreMixedCount
      || b.genderFit.coreScoreTotal - a.genderFit.coreScoreTotal
      || b.genderFit.scoreTotal - a.genderFit.scoreTotal
      ||
      b.score - a.score
      || b.secondCandidateCount + b.thirdCandidateCount - (a.secondCandidateCount + a.thirdCandidateCount)
      || a.secondStroke - b.secondStroke
      || a.thirdStroke - b.thirdStroke
    ));

  const preferred = sorted.filter((item) => item.genderFit.coreBadCount === 0 && item.genderFit.coreMixedCount === 0);
  const fallback = sorted.filter((item) => item.genderFit.coreBadCount === 0);
  const source = preferred.length >= Math.min(limit, 6)
    ? preferred
    : fallback.length >= Math.min(limit, 6)
      ? fallback
      : sorted;

  return source.slice(0, limit);
};
